import moment from 'moment'
import React, { useContext } from 'react'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import GeoContext from 'contexts/geoContext'
import { isOneWayFlight } from 'lib/flights/flightUtils'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { extractOfferIdsFromOrder } from 'tripPlanner/components/EntryPoints/utils'
import BookingItemCard from 'tripPlanner/components/ItemCard/BookingItemCard'
import { useFlightDetailsForItem } from 'tripPlanner/hooks/api/flights'
import { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { concatStringWithDot } from 'tripPlanner/utils'
import useOffers from 'hooks/Offers/useOffers'

type Props = {
  orderFlightItem: App.OrderFlightItem
  showBorder?: boolean
  order: App.Order
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function FlightBookingCard(props: Props) {
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { orderFlightItem, showBorder, order, onSelect } = props
  const { orderId, itemId } = orderFlightItem

  const [offers, isOffersLoading] = useOffers(extractOfferIdsFromOrder(order))
  const { data: flightDetails, isLoading: isFlightDetailsLoading } =
    useFlightDetailsForItem({ orderId, itemId })

  const { fares, tripType } = flightDetails?.journeyFares || {}

  const [departing, returning] = fares ?? []

  const departingFlight = departing?.flightGroup.flights[0]
  const isOneWayFare = isOneWayFlight(tripType ?? '')

  const arrivalFlight = isOneWayFare ?
    departing?.flightGroup.flights[departing?.flightGroup.flights.length - 1] :
    returning?.flightGroup.flights[0]

  let arrivalFlightLabel = ''
  if (isOneWayFare) {
    arrivalFlightLabel =
      arrivalFlight?.arrivalAirportName + ', ' + arrivalFlight?.arrivalCountry
  } else {
    arrivalFlightLabel =
      arrivalFlight?.departureAirportName +
      ', ' +
      arrivalFlight?.departureCountry
  }

  const isFetching = isOffersLoading || isFlightDetailsLoading

  const renderStartAndEndDates = () => {
    let startAndEndDates = ''
    if (departing?.flightGroup.flights[0].departingDate) {
      startAndEndDates += moment(
        departing?.flightGroup.flights[0].departingDate,
      ).format('DD MMM')
    }

    if (returning?.flightGroup.flights[0].arrivalDate) {
      startAndEndDates += ` - ${moment(
        returning.flightGroup.flights[0].arrivalDate,
      ).format('DD MMM YYYY')}`
    }

    return startAndEndDates
  }

  if (isFetching || !departingFlight || !arrivalFlight) {
    return <LoadingIndicator inline />
  }

  return (
    <BookingItemCard
      heading={`${departingFlight.departureAirportName}, ${departingFlight.departureCountry} to ${arrivalFlightLabel}`}
      subHeading={concatStringWithDot(
        renderStartAndEndDates(),
        formatDynamicAmountWithDollarType(
          orderFlightItem.total,
          currentCurrency,
          currentRegionCode,
        ),
      )}
      showBorder={showBorder}
      imgUrl={departing?.carrierLogo}
      onSelect={(checked) => {
        if (onSelect) {
          onSelect(order, offers, checked)
        }
      }}
    />
  )
}

export default FlightBookingCard
